import {
  handleSubmitMultilingualForm,
  useCreateEshopPage,
  useGetEshopFormData,
  useUpdateEshopPage,
} from "@/api/cms";
import ArticleList from "@/components/cms/e-shop/ArticleList";
import { CmsPageAppearanceConfig } from "@/components/cms/theme/CmsPageAppearanceConfig";
import { EshopForm } from "@/components/cms/e-shop/EshopPageForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import {
  constructCmsLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageLocaleBaseSchema } from "@/lib/cms/cms-types";
import {
  buildCmsPagePatchPayload,
  pageThemeFromDto,
} from "@/lib/cms/page-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  cmsPageLocaleBaseSchema,
);

type FormData = z.infer<typeof formSchema>;

export const EshopEditor = () => {
  const { t } = useTranslation();
  const { websiteLanguages } = useCmsWebsiteLanguages();
  const { instanceData } = useAuth();

  const { data: eShopData } = useGetEshopFormData();
  const update = useUpdateEshopPage();
  const create = useCreateEshopPage();

  const defaultValues: FormData = useMemo(
    () =>
      constructCmsLocaleDict((lang) => {
        const eShop = eShopData?.[lang];
        return {
          published: eShop?.published ?? false,
          pageName: eShop?.pageName ?? "",
          ...pageThemeFromDto(eShop),
        };
      }, websiteLanguages),
    [eShopData, websiteLanguages],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { reset } = form;

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (
      !hasInitialized.current &&
      eShopData != null &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [reset, defaultValues, eShopData]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = eShopData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const {
        pageName,
        published,
        pageBackgroundColorId,
        pageTextColorMode,
        pageTextColorId,
        ...content
      } = localeData;
      const pageNameStr = pageName ?? "";

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: buildCmsPagePatchPayload({
            pageName: pageNameStr,
            published,
            pageBackgroundColorId,
            pageTextColorMode,
            pageTextColorId,
            content,
          }),
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("contactSuccessMessage"),
              duration: 4000,
              variant: "success",
            });
          },
          onError: (error) => {
            console.error("Error", error);
            toast({
              title: t("error"),
              description: t("contactErrorMessage"),
              variant: "error",
              duration: 4000,
            });
          },
        },
      );
    });
  };

  return (
    <>
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-e-shop">
        <CmsPageAppearanceConfig formId="e-shop" />
        <form
          onSubmit={handleSubmitMultilingualForm(form, eShopData, onSubmit)}
          id="e-shop"
          className="flex w-full flex-col gap-6"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <EshopForm language={lang} />}
          />
        </form>
              </FormFieldVisibilityProvider>
      </FormProvider>
      <ArticleList />
    </>
  );
};

const EshopCMS = () => {
  const { t } = useTranslation();
  return (
    <CMSLayout
      pageName={t("cmsTabEshop")}
      formIds={["e-shop"]}
      className="flex flex-col"
    >
      <EshopEditor />
    </CMSLayout>
  );
};

export default EshopCMS;
